import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { I18nManager, Platform } from 'react-native';
import * as Updates from 'expo-updates';
import ar from '../locales/ar.json';
import en from '../locales/en.json';
import logger from '../utils/logger';

const LANGUAGE_KEY = '@app_language';
const RTL_SET_KEY = '@rtl_configured';

const resources = {
  ar: { translation: ar },
  en: { translation: en },
};

const initI18n = async () => {
  try {
    let savedLanguage = await AsyncStorage.getItem(LANGUAGE_KEY);
    const rtlConfigured = await AsyncStorage.getItem(RTL_SET_KEY);

    if (!savedLanguage) {
      savedLanguage = 'ar'; // Default to Arabic (target audience)
      await AsyncStorage.setItem(LANGUAGE_KEY, savedLanguage);
    }

    await i18n.use(initReactI18next).init({
      resources,
      lng: savedLanguage,
      fallbackLng: 'en',
      interpolation: {
        escapeValue: false,
      },
    });

    // Set RTL for Arabic - only reload if RTL state doesn't match saved language
    const isRTL = savedLanguage === 'ar';
    const currentRTL = I18nManager.isRTL;

    if (currentRTL !== isRTL) {
      // Check if we already tried to configure RTL to avoid infinite loop
      const expectedRTL = rtlConfigured === 'true';

      if (isRTL !== expectedRTL) {
        // RTL state needs to change
        I18nManager.forceRTL(isRTL);
        I18nManager.allowRTL(isRTL);
        await AsyncStorage.setItem(RTL_SET_KEY, String(isRTL));

        // Reload app to apply RTL changes (native only)
        if (Platform.OS !== 'web') {
          try {
            await Updates.reloadAsync();
          } catch (e) {
            // Reload may fail in development, ignore
          }
        }
      }
    } else {
      // RTL is correct, update the flag
      await AsyncStorage.setItem(RTL_SET_KEY, String(isRTL));
    }
  } catch (error) {
    logger.error('i18n init error:', error);
    // Initialize with default on error
    await i18n.use(initReactI18next).init({
      resources,
      lng: 'en',
      fallbackLng: 'en',
      interpolation: {
        escapeValue: false,
      },
    });
  }
};

export const changeLanguage = async (language: string) => {
  try {
    // First save language to storage BEFORE anything else
    await AsyncStorage.setItem(LANGUAGE_KEY, language);

    // Change i18n language
    await i18n.changeLanguage(language);

    // Handle RTL
    const isRTL = language === 'ar';
    const currentRTL = I18nManager.isRTL;

    if (currentRTL !== isRTL) {
      // Update RTL settings
      I18nManager.forceRTL(isRTL);
      I18nManager.allowRTL(isRTL);
      await AsyncStorage.setItem(RTL_SET_KEY, String(isRTL));

      // Reload app to apply RTL changes (native only)
      if (Platform.OS !== 'web') {
        try {
          await Updates.reloadAsync();
        } catch (e) {
          // Reload may fail in development
          logger.log('Reload not available, RTL will apply on next restart');
        }
      }
    } else {
      // Update the flag even if RTL doesn't change
      await AsyncStorage.setItem(RTL_SET_KEY, String(isRTL));
    }
  } catch (error) {
    logger.error('changeLanguage error:', error);
    throw error;
  }
};

// Export function to get current saved language
export const getSavedLanguage = async (): Promise<string> => {
  try {
    const lang = await AsyncStorage.getItem(LANGUAGE_KEY);
    return lang || 'en';
  } catch {
    return 'en';
  }
};

initI18n();

export default i18n;
